import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import { Grid } from '@material-ui/core';
import SideMenu from './SideMenu';
import NotificationButton from './NotificationButton';
import LandingNavLinks from './LandingNavLinks';
import LandingMobileNav from './LandingMobileNav';

import logo from '../../images/barbers-limited-text-logo.png';

const HeaderBanner = () => {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth0();

  React.useEffect(() => {
    // Auth0 reports isAuthenticated: false initially, before it has
    // resolved whether a session exists (in particular right after the
    // /authenticate callback redirect, while it's still exchanging the
    // code for tokens). Acting on that transient false here would wipe
    // `role` (set on the login page, before the redirect, and never
    // re-set afterward) before the partner/role lookup in
    // useHydratedSession gets a chance to use it.
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      // Don't wipe reg_* keys — user may be mid-registration
      ['user', 'usertoken'].forEach((k) => sessionStorage.removeItem(k));
      ['email', 'role', 'user', 'token', 'notifications'].forEach((k) =>
        localStorage.removeItem(k)
      )
    }
  }, [isLoading, isAuthenticated]);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );

  const user = sessionStorage.getItem('user') !== null;

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar position="fixed" color="inherit">
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            justifyContent: user ? 'center' : 'space-between',
            position: 'relative',
          }}
        >
          {/* Menu button - positioned absolutely on the left */}
          {user && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                display: { xs: 'flex', md: 'block', lg: 'none' },
              }}
            >
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'block' },
                }}
              >
                <Grid component={Box} xs={12} item>
                  <SideMenu />
                </Grid>
              </Menu>
            </Box>
          )}

          {/* Logo: centered for logged-in app shell, left-aligned on the public site */}
          <Box
            sx={{ cursor: 'pointer', mx: user ? 'auto' : 0 }}
            onClick={() => navigate(user ? '/dashboard' : '/')}
          >
            <img
              alt={'Barbers Limited'}
              src={logo}
              style={{ height: 85, width: 'auto' }}
            />
          </Box>

          {/* Notification button - positioned absolutely on the right */}
          {user && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
              }}
            >
              <NotificationButton />
            </Box>
          )}

          {/* Public navigation for anonymous visitors */}
          {!user && (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <LandingNavLinks />
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <LandingMobileNav />
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default HeaderBanner;
